import { LivesQuery, LiveWhereInput, QueryMode } from '@/__generated__/graphql';
import { useMutation, useQuery } from '@apollo/client';
import { ActionIcon, Group, Paper, Stack, TextInput } from '@mantine/core';
import { useDebouncedValue, useLocalStorage } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import {
  IconCheck,
  IconEdit,
  IconEye,
  IconNavigation,
  IconPlus,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LiveUpsertModal } from '../components/LiveUpsertModal';
import { deleteLiveDocument, toggleLiveActiveDocument } from '../graphql/mutations';
import { countLivesDocument, livesDocument } from '../graphql/queries';

export function LiveList() {
  const navigate = useNavigate();

  const [filterStatus, setFilterStatus] = useLocalStorage<LiveWhereInput>({
    key: 'live-list-filter',
    defaultValue: {},
  });

  const [where] = useDebouncedValue(filterStatus, 500);

  const [sortStatus, setSortStatus] = useLocalStorage<
    DataTableSortStatus<LivesQuery['lives'][number]>
  >({
    key: 'live-list-sort',
    defaultValue: {
      columnAccessor: 'date',
      direction: 'desc',
    },
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useLocalStorage({
    key: 'live-list-page-size',
    defaultValue: 10,
  });

  const { data: count } = useQuery(countLivesDocument, { variables: { where } });

  const { data, loading } = useQuery(livesDocument, {
    variables: {
      skip: pageSize * page - pageSize,
      take: pageSize,
      orderBy: {
        [sortStatus.columnAccessor]: sortStatus.direction,
      },
      where,
    },
  });

  const [toggleLiveActive, toggleLiveActiveMutation] = useMutation(toggleLiveActiveDocument, {
    refetchQueries: 'active',
  });

  const [deleteLive, deleteLiveMutation] = useMutation(deleteLiveDocument, {
    refetchQueries: 'active',
  });

  return (
    <Stack>
      <Paper p="md" withBorder component={Group}>
        <Group>
          <TextInput
            placeholder="Názov..."
            value={filterStatus.name?.contains ?? ''}
            onChange={(event) => {
              setFilterStatus((prev) => ({
                ...prev,
                name: { contains: event.currentTarget.value, mode: QueryMode.Insensitive },
              }));
            }}
          />
          <TextInput
            placeholder="Popis..."
            value={filterStatus.description?.contains ?? ''}
            onChange={(event) => {
              setFilterStatus((prev) => ({
                ...prev,
                description: { contains: event.currentTarget.value, mode: QueryMode.Insensitive },
              }));
            }}
          />
        </Group>
      </Paper>
      <DataTable
        withTableBorder
        striped
        highlightOnHover
        textSelectionDisabled
        pinLastColumn
        borderRadius="sm"
        minHeight={200}
        records={data?.lives}
        fetching={loading}
        totalRecords={count?.countLives}
        recordsPerPage={pageSize}
        page={page}
        onPageChange={setPage}
        recordsPerPageOptions={[5, 10, 25, 50]}
        onRecordsPerPageChange={setPageSize}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        columns={[
          { accessor: 'name', title: 'Názov', sortable: true },
          { accessor: 'description', title: 'Popis', sortable: true },
          {
            accessor: 'date',
            title: 'Dátum',
            render: ({ date }) => dayjs(date).format('DD/MM/YYYY'),
            sortable: true,
          },
          {
            accessor: 'active',
            title: 'Stav',
            render: ({ id, active }) => (
              <ActionIcon
                size="sm"
                variant="subtle"
                color={active ? 'teal' : 'red'}
                onClick={() => void toggleLiveActive({ variables: { where: { id } } })}
                loading={toggleLiveActiveMutation.loading}
              >
                {active ? <IconCheck size={16} /> : <IconX size={16} />}
              </ActionIcon>
            ),
            sortable: true,
          },
          {
            accessor: 'orders',
            title: 'Objednávky',
            render: ({ _count }) => _count.orders,
          },
          {
            accessor: 'products',
            title: 'Produkty',
            render: ({ _count }) => _count.products,
          },
          {
            accessor: 'actions',
            textAlign: 'right',
            title: (
              <ActionIcon
                size="sm"
                variant="subtle"
                onClick={() =>
                  modals.open({ title: 'Vytvoriť LIVE', children: <LiveUpsertModal /> })
                }
              >
                <IconPlus size={16} />
              </ActionIcon>
            ),
            render: (live) => (
              <Group gap={4} justify="right" wrap="nowrap">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="green"
                  onClick={() => void navigate(live.id)}
                >
                  <IconEye size={16} />
                </ActionIcon>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="green"
                  onClick={() => void navigate(`/tablet/${live.id}`)}
                >
                  <IconNavigation size={16} />
                </ActionIcon>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="blue"
                  onClick={() =>
                    modals.open({
                      title: `Upraviť LIVE ${live.id}`,
                      children: <LiveUpsertModal live={live} />,
                    })
                  }
                >
                  <IconEdit size={16} />
                </ActionIcon>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="red"
                  loading={deleteLiveMutation.loading}
                  onClick={() =>
                    modals.openConfirmModal({
                      title: `Zmazať LIVE ${live.name} z dňa ${dayjs(live.date).format('DD/MM/YYYY')}?`,
                      children: 'Naozaj chcete zmazať tento LIVE?',
                      centered: true,
                      labels: { confirm: 'Zmazať LIVE', cancel: 'Nie' },
                      confirmProps: { color: 'red' },
                      onConfirm: () => void deleteLive({ variables: { where: { id: live.id } } }),
                    })
                  }
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ),
          },
        ]}
      />
    </Stack>
  );
}
